import axios from 'axios'
import { useMailAuthStore } from '@/stores/mailAuth'
import type {
  MailboxLoginRequest,
  MailboxLoginResponse,
  MailboxSelfInfo,
  Vacation,
  UpdateVacationRequest,
  AppPasswordListResponse,
  CreateAppPasswordRequest,
  CreateAppPasswordResponse,
  IdentityListResponse,
  CreateIdentityRequest,
  Identity,
  LoginHistoryResponse
} from '@/types/dashboard/mail'

function getMailAxiosConfig(token: string) {
  return {
    baseURL: import.meta.env.VITE_BASE_URL,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf-8',
      Authorization: `Bearer ${token}`
    },
    timeout: 30_000
  }
}

export function useMailAuth() {
  const login = async (data: MailboxLoginRequest): Promise<MailboxLoginResponse> => {
    const response = await axios.post<MailboxLoginResponse>(
      '/mail/auth',
      data,
      {
        baseURL: import.meta.env.VITE_BASE_URL,
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=utf-8'
        },
        timeout: 30_000
      }
    )
    return response.data
  }

  return {
    login
  }
}

export function useMailSelf() {
  const mailAuthStore = useMailAuthStore()

  const getConfig = () => {
    const token = mailAuthStore.getToken
    if (!token) {
      throw new Error('Not authenticated')
    }
    return getMailAxiosConfig(token)
  }

  const getInfo = async (): Promise<MailboxSelfInfo> => {
    const response = await axios.get<MailboxSelfInfo>('/mail/me', getConfig())
    return response.data
  }

  const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
    await axios.post(
      '/mail/me/password',
      { current_password: currentPassword, new_password: newPassword },
      getConfig()
    )
  }

  const getVacation = async (): Promise<Vacation> => {
    const response = await axios.get<Vacation>('/mail/me/vacation', getConfig())
    return response.data
  }

  const updateVacation = async (data: UpdateVacationRequest): Promise<Vacation> => {
    const response = await axios.patch<Vacation>('/mail/me/vacation', data, getConfig())
    return response.data
  }

  const listIdentities = async (): Promise<IdentityListResponse> => {
    const response = await axios.get<IdentityListResponse>('/mail/me/identity', getConfig())
    return response.data
  }

  const createIdentity = async (data: CreateIdentityRequest): Promise<Identity> => {
    const response = await axios.post<Identity>('/mail/me/identity', data, getConfig())
    return response.data
  }

  const deleteIdentity = async (identityId: string): Promise<void> => {
    await axios.delete(`/mail/me/identity/${identityId}`, getConfig())
  }

  const listAppPasswords = async (): Promise<AppPasswordListResponse> => {
    const response = await axios.get<AppPasswordListResponse>('/mail/me/app-password', getConfig())
    return response.data
  }

  const createAppPassword = async (data: CreateAppPasswordRequest): Promise<CreateAppPasswordResponse> => {
    const response = await axios.post<CreateAppPasswordResponse>('/mail/me/app-password', data, getConfig())
    return response.data
  }

  const deleteAppPassword = async (passwordId: number): Promise<void> => {
    await axios.delete(`/mail/me/app-password/${passwordId}`, getConfig())
  }

  const getLoginHistory = async (limit = 50): Promise<LoginHistoryResponse> => {
    const response = await axios.get<LoginHistoryResponse>(`/mail/me/logins?limit=${limit}`, getConfig())
    return response.data
  }

  return {
    getInfo,
    changePassword,
    getVacation,
    updateVacation,
    listIdentities,
    createIdentity,
    deleteIdentity,
    listAppPasswords,
    createAppPassword,
    deleteAppPassword,
    getLoginHistory
  }
}
